import { useState, useEffect, useCallback } from "react";
import { api, formatDate, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Edit, UserCircle, ArrowLeft, ChevronRight, GraduationCap, AlertTriangle, List, MapPin, Copy, Users } from "lucide-react";
import { DisputeBadge } from "@/components/admin/DisputeBadge";
import TravelBonusTab from "../components/TravelBonusTab";
import HRPilotageTab from "../components/HRPilotageTab";
import EmployeeDetailView from "../components/employees/EmployeeDetailView";
import {
  EMPLOYEE_TYPES, SIZES_TOP, SIZES_BOTTOM, SHOE_SIZES, defaultEmployeeForm,
} from "@/lib/employeeConstants";
import { useTableColumns } from "@/hooks/useTableColumns";
import { ColumnsToggle, SortButton } from "@/components/TableControls";

export const EmployeesPage = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(defaultEmployeeForm);
  const [showListing, setShowListing] = useState(false);
  const [listing, setListing] = useState([]);
  const [trainingAlerts, setTrainingAlerts] = useState({ alerts: [], count: 0 });
  const [listingLoading, setListingLoading] = useState(false);
  const [showFormer, setShowFormer] = useState(false);

  // Colonnes employés (tri + masquage)
  const EMPLOYEE_COLUMNS = [
    { key: "name", label: "Nom" },
    { key: "type", label: "Type" },
    { key: "phone", label: "Téléphone" },
    { key: "entry_date", label: "Entrée" },
    { key: "exit_date", label: "Sortie" },
    { key: "status", label: "Statut" },
    { key: "actions", label: "Actions", sortable: false, always: true },
  ];
  const {
    isVisible: isEmpColVisible,
    toggleVisibility: toggleEmpCol,
    handleSort: handleEmpSort,
    sortArrow: empSortArrow,
    sortItems: sortEmps,
    orderedColumns: orderedEmpColumns,
    moveColumn: moveEmpColumn,
    resetOrder: resetEmpOrder,
  } = useTableColumns({ storageKey: "employees-table", columns: EMPLOYEE_COLUMNS });

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const [empRes, alertsRes] = await Promise.all([
        api.getEmployees(),
        api.getTrainingAlerts()
      ]);
      setEmployees(empRes.data);
      setTrainingAlerts(alertsRes.data);
    } catch (e) {
      toast.error("Erreur lors du chargement des salariés");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchListing = async () => {
    try {
      setListingLoading(true);
      const res = await api.getTrainingsListing();
      setListing(res.data);
      setShowListing(true);
    } catch (e) {
      toast.error("Erreur chargement listing");
    } finally {
      setListingLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const openCreate = () => {
    setFormData(defaultEmployeeForm);
    setEditMode(false);
    setDialogOpen(true);
  };

  const openEdit = (emp) => {
    setFormData({
      ...defaultEmployeeForm,
      ...emp,
      size_top_custom: emp.size_top_custom || "",
      size_bottom_custom: emp.size_bottom_custom || "",
      shoe_size: emp.shoe_size || ""
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const openDuplicate = (emp) => {
    setFormData({
      ...defaultEmployeeForm,
      size_top: emp.size_top || "",
      size_top_custom: emp.size_top_custom || "",
      size_bottom: emp.size_bottom || "",
      size_bottom_custom: emp.size_bottom_custom || "",
      shoe_size: emp.shoe_size || "",
      employee_type: emp.employee_type || "ouvrier",
      comments: emp.comments || "",
      cp_balance: emp.cp_balance ?? 25,
      rtt_salarie_balance: emp.rtt_salarie_balance ?? 8.5,
      rtt_entreprise_balance: emp.rtt_entreprise_balance ?? 8.5
    });
    setEditMode(false);
    setDialogOpen(true);
    toast.info(`Informations contractuelles de ${emp.first_name} ${emp.last_name} copiées — complétez nom, prénom et contact.`);
  };

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name) {
      toast.error("Nom et prénom obligatoires");
      return;
    }
    try {
      const payload = {
        ...formData,
        cp_balance: parseFloat(formData.cp_balance) || 0,
        rtt_salarie_balance: parseFloat(formData.rtt_salarie_balance) || 0,
        rtt_entreprise_balance: parseFloat(formData.rtt_entreprise_balance) || 0,
        entry_date: formData.entry_date || null,
        exit_date: formData.exit_date || null,
        is_interim: !!formData.is_interim,
        interim_hourly_rate: formData.is_interim && formData.interim_hourly_rate
          ? parseFloat(formData.interim_hourly_rate)
          : null,
        interim_agency_name: formData.is_interim ? (formData.interim_agency_name || null) : null,
        interim_agency_phone: formData.is_interim ? (formData.interim_agency_phone || null) : null,
        interim_agency_email: formData.is_interim ? (formData.interim_agency_email || null) : null,
        interim_agency_address: formData.is_interim ? (formData.interim_agency_address || null) : null,
      };
      if (editMode) {
        await api.updateEmployee(formData.id, payload);
        toast.success("Salarié mis à jour");
      } else {
        await api.createEmployee(payload);
        toast.success("Salarié créé");
      }
      setDialogOpen(false);
      fetchEmployees();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteEmployee(id);
      toast.success("Salarié supprimé");
      if (selectedEmployee?.id === id) setSelectedEmployee(null);
      fetchEmployees();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  // Le Dialog d'édition est toujours rendu (state-controlled `dialogOpen`).
  // On le déclare ICI pour pouvoir le rendre à la fois dans la vue liste ET
  // dans la vue détail — sinon le early-return `if (selectedEmployee)`
  // empêche son affichage (bug "Modifier ouvre rien").

  if (selectedEmployee) {
    return (
      <>
        <EmployeeDetailView
          employee={selectedEmployee}
          onBack={() => {
            setSelectedEmployee(null);
            fetchEmployees();
          }}
          onEdit={() => openEdit(selectedEmployee)}
        />
        <EmployeeEditDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          editMode={editMode}
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
        />
      </>
    );
  }

  if (showListing) {
    return (
      <div className="space-y-6" data-testid="trainings-listing">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => setShowListing(false)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Retour
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Listing Formations</h1>
            <p className="text-muted-foreground">Toutes les formations par salarié</p>
          </div>
        </div>

        {listing.map(emp => (
          <Card key={emp.employee_id} className={emp.has_alert ? "border-amber-300" : ""}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                {emp.has_alert && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                {emp.employee_name}
                <Badge className={EMPLOYEE_TYPES[emp.employee_type]?.color || ""}>{EMPLOYEE_TYPES[emp.employee_type]?.label || emp.employee_type}</Badge>
                <span className="text-sm text-muted-foreground font-normal">({emp.training_count} formation{emp.training_count > 1 ? "s" : ""})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {emp.trainings.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Aucune formation</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Formation</TableHead>
                      <TableHead>Organisme</TableHead>
                      <TableHead>Date formation</TableHead>
                      <TableHead>Fin de validité</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {emp.trainings.map(t => (
                      <TableRow key={t.id} className={t.status === "expired" ? "bg-red-50" : t.status === "expiring_soon" ? "bg-amber-50" : ""}>
                        <TableCell className="font-medium">{t.name}</TableCell>
                        <TableCell>{t.organization || "-"}</TableCell>
                        <TableCell>{formatDate(t.training_date)}</TableCell>
                        <TableCell>{t.expiry_date ? formatDate(t.expiry_date) : "-"}</TableCell>
                        <TableCell>
                          {t.status === "expired" && <Badge variant="destructive">Expirée ({Math.abs(t.days_remaining)}j)</Badge>}
                          {t.status === "expiring_soon" && <Badge className="bg-amber-500 text-white">{t.days_remaining}j restants</Badge>}
                          {t.status === "valid" && <Badge className="bg-green-100 text-green-800">Valide</Badge>}
                          {!t.expiry_date && <Badge variant="outline">-</Badge>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  const activeCount = employees.filter(e => !e.exit_date).length;
  const ouvriersCount = employees.filter(e => e.employee_type === "ouvrier" && !e.exit_date).length;

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Salariés</h1>
          <p className="text-muted-foreground">Gérez l'effectif, absences, primes et contraventions</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="show-former-employees-toggle">
            <input type="checkbox" checked={showFormer} onChange={(e) => setShowFormer(e.target.checked)} className="h-4 w-4" />
            Afficher ex-salariés
          </label>
          <Button variant="outline" onClick={fetchListing} disabled={listingLoading} data-testid="listing-formations-btn">
            {listingLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <List className="h-4 w-4 mr-2" />}
            Listing formations
          </Button>
          <Button onClick={openCreate} data-testid="add-employee-btn">
            <Plus className="h-4 w-4 mr-2" /> Ajouter un salarié
          </Button>
        </div>
      </div>

      {trainingAlerts.count > 0 && (
        <Card className="border-2 border-amber-300 bg-amber-50" data-testid="training-alerts-banner">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-start gap-3">
              <GraduationCap className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800 text-sm">{trainingAlerts.count} formation(s) expirent dans moins de 9 mois</p>
                <div className="mt-2 space-y-1">
                  {trainingAlerts.alerts.slice(0, 5).map((a, i) => (
                    <div key={i} className="text-sm flex flex-col sm:flex-row sm:justify-between gap-1">
                      <span><span className="font-medium">{a.employee_name}</span> - {a.training_name}</span>
                      <span className={`font-bold ${a.days_remaining < 0 ? "text-destructive" : "text-amber-700"}`}>
                        {a.days_remaining < 0 ? `Expirée (${Math.abs(a.days_remaining)}j)` : `${a.days_remaining}j restants`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="list" className="space-y-4">
        <TabsList>
          <TabsTrigger value="list" data-testid="main-tab-list"><UserCircle className="h-4 w-4 mr-1" /> Liste des salariés</TabsTrigger>
          <TabsTrigger value="pilotage" data-testid="main-tab-pilotage"><Users className="h-4 w-4 mr-1" /> Pilotage RH</TabsTrigger>
          <TabsTrigger value="travel-bonus" data-testid="main-tab-travel-bonus"><MapPin className="h-4 w-4 mr-1" /> Primes de petit déplacement</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">Effectif total</div><div className="text-3xl font-bold">{activeCount}</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">Ouvriers actifs</div><div className="text-3xl font-bold text-orange-600">{ouvriersCount}</div></CardContent></Card>
            <Card><CardContent className="pt-6"><div className="text-sm text-muted-foreground">Ex-salariés</div><div className="text-3xl font-bold text-slate-500">{employees.length - activeCount}</div></CardContent></Card>
          </div>

          <Card>
            <CardHeader><CardTitle>Liste des salariés</CardTitle></CardHeader>
            <CardContent>
              {employees.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucun salarié. Cliquez sur "Ajouter un salarié".</p>
              ) : (
                <>
                <div className="flex items-center justify-end pb-2">
                  <ColumnsToggle columns={EMPLOYEE_COLUMNS} orderedColumns={orderedEmpColumns} isVisible={isEmpColVisible} toggleVisibility={toggleEmpCol} moveColumn={moveEmpColumn} resetOrder={resetEmpOrder} testId="employees-columns-toggle" />
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      {orderedEmpColumns.filter(c => isEmpColVisible(c.key)).map(col => {
                        if (col.key === 'actions') return <TableHead key={col.key}>Actions</TableHead>;
                        return (
                          <TableHead key={col.key}>
                            <SortButton columnKey={col.key} label={col.label} sortArrow={empSortArrow} handleSort={handleEmpSort} />
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortEmps(employees.filter(e => showFormer || !e.exit_date), {
                      name: (e) => `${e.last_name || ""} ${e.first_name || ""}`.trim(),
                      type: (e) => EMPLOYEE_TYPES[e.employee_type]?.label || e.employee_type,
                      status: (e) => (e.exit_date ? "Parti" : "Actif"),
                    }).map(emp => {
                      const renderEmpCell = (key) => {
                        switch(key) {
                          case 'name': return (
                            <TableCell key={key} className="font-medium">
                              <div className="flex items-center gap-2">
                                <button onClick={() => setSelectedEmployee(emp)} className="text-primary hover:underline text-left" data-testid={`employee-name-${emp.id}`}>
                                  {emp.last_name} {emp.first_name}
                                </button>
                                <DisputeBadge thirdPartyId={emp.id} />
                              </div>
                            </TableCell>
                          );
                          case 'type': return (
                            <TableCell key={key}>
                              <div className="flex flex-wrap items-center gap-1">
                                <Badge className={EMPLOYEE_TYPES[emp.employee_type]?.color}>
                                  {EMPLOYEE_TYPES[emp.employee_type]?.label || emp.employee_type}
                                </Badge>
                                {emp.is_interim && (
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`interim-badge-${emp.id}`}>
                                    Intérim
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                          );
                          case 'phone': return <TableCell key={key}>{emp.phone || "-"}</TableCell>;
                          case 'entry_date': return <TableCell key={key}>{formatDate(emp.entry_date)}</TableCell>;
                          case 'exit_date': return <TableCell key={key}>{formatDate(emp.exit_date)}</TableCell>;
                          case 'status': return (
                            <TableCell key={key}>
                              {emp.exit_date ? <Badge variant="outline">Parti</Badge> : <Badge className="bg-green-100 text-green-800">Actif</Badge>}
                            </TableCell>
                          );
                          case 'actions': return (
                            <TableCell key={key}>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => setSelectedEmployee(emp)} data-testid={`view-employee-${emp.id}`}>
                                  <ChevronRight className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openEdit(emp)} data-testid={`edit-employee-${emp.id}`}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openDuplicate(emp)} data-testid={`duplicate-employee-${emp.id}`} title="Dupliquer">
                                  <Copy className="h-4 w-4 text-blue-500" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm" data-testid={`delete-employee-${emp.id}`}>
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Supprimer ce salarié ?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Toutes les absences, primes et PV associés seront également supprimés. Cette action est irréversible.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDelete(emp.id)}>Supprimer</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          );
                          default: return null;
                        }
                      };
                      return (
                        <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                          {orderedEmpColumns.filter(col => isEmpColVisible(col.key)).map(col => renderEmpCell(col.key))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pilotage">
          <HRPilotageTab />
        </TabsContent>

        <TabsContent value="travel-bonus">
          <TravelBonusTab />
        </TabsContent>
      </Tabs>

      <EmployeeEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editMode={editMode}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
      />
    </div>
  );
};

/**
 * Dialog d'édition / création d'un salarié — extrait pour pouvoir être
 * rendu à la fois dans la vue liste ET dans la vue détail
 * (sinon le early-return `if (selectedEmployee)` empêche l'affichage).
 */
const EmployeeEditDialog = ({ open, onOpenChange, editMode, formData, setFormData, handleSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="employee-edit-dialog">
      <DialogHeader>
        <DialogTitle>{editMode ? "Modifier le salarié" : "Nouveau salarié"}</DialogTitle>
        <DialogDescription>Informations personnelles et contrat</DialogDescription>
      </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Prénom *</Label>
              <Input value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} data-testid="emp-firstname" />
            </div>
            <div>
              <Label>Nom *</Label>
              <Input value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} data-testid="emp-lastname" />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
            </div>
            <div>
              <Label>Type de salarié</Label>
              <Select value={formData.employee_type} onValueChange={v => setFormData({...formData, employee_type: v})}>
                <SelectTrigger data-testid="emp-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(EMPLOYEE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Adresse</Label>
              <Input value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} />
            </div>
            <div>
              <Label>Taille haut</Label>
              <Select value={formData.size_top} onValueChange={v => setFormData({...formData, size_top: v})}>
                <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>
                  {SIZES_TOP.map(s => <SelectItem key={s} value={s}>{s === "custom" ? "Personnalisé…" : s}</SelectItem>)}
                </SelectContent>
              </Select>
              {formData.size_top === "custom" && (
                <Input className="mt-2" placeholder="Taille personnalisée" value={formData.size_top_custom} onChange={e => setFormData({...formData, size_top_custom: e.target.value})} />
              )}
            </div>
            <div>
              <Label>Taille bas</Label>
              <Select value={formData.size_bottom} onValueChange={v => setFormData({...formData, size_bottom: v})}>
                <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>
                  {SIZES_BOTTOM.map(s => <SelectItem key={s} value={s}>{s === "custom" ? "Personnalisé…" : s}</SelectItem>)}
                </SelectContent>
              </Select>
              {formData.size_bottom === "custom" && (
                <Input className="mt-2" placeholder="Taille personnalisée" value={formData.size_bottom_custom} onChange={e => setFormData({...formData, size_bottom_custom: e.target.value})} />
              )}
            </div>
            <div>
              <Label>Pointure</Label>
              <Select value={formData.shoe_size} onValueChange={v => setFormData({...formData, shoe_size: v})}>
                <SelectTrigger data-testid="emp-shoe-size"><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>
                  {SHOE_SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date d'entrée</Label>
              <Input type="date" value={formData.entry_date || ""} onChange={e => setFormData({...formData, entry_date: e.target.value})} data-testid="emp-entry-date" />
            </div>
            <div>
              <Label>Date de sortie (vide si toujours dans l'effectif)</Label>
              <Input type="date" value={formData.exit_date || ""} onChange={e => setFormData({...formData, exit_date: e.target.value})} data-testid="emp-exit-date" />
            </div>
            <div>
              <Label>CP annuels</Label>
              <Input type="number" step="0.5" value={formData.cp_balance} onChange={e => setFormData({...formData, cp_balance: e.target.value})} />
            </div>
            <div>
              <Label>RTT salarié / an</Label>
              <Input type="number" step="0.5" value={formData.rtt_salarie_balance} onChange={e => setFormData({...formData, rtt_salarie_balance: e.target.value})} />
            </div>
            <div>
              <Label>RTT entreprise / an</Label>
              <Input type="number" step="0.5" value={formData.rtt_entreprise_balance} onChange={e => setFormData({...formData, rtt_entreprise_balance: e.target.value})} />
            </div>
            <div className="md:col-span-2">
              <Label>Commentaires</Label>
              <Textarea value={formData.comments} onChange={e => setFormData({...formData, comments: e.target.value})} rows={3} />
            </div>

            <div className="md:col-span-2 border rounded p-3 bg-amber-50/40 space-y-3" data-testid="emp-interim-section">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_interim"
                  checked={!!formData.is_interim}
                  onCheckedChange={(v) => setFormData({ ...formData, is_interim: !!v })}
                  data-testid="emp-is-interim"
                />
                <Label htmlFor="is_interim" className="cursor-pointer font-medium">
                  Salarié intérimaire
                </Label>
              </div>
              {formData.is_interim && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Taux horaire facturé (€/h)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.interim_hourly_rate}
                      onChange={(e) => setFormData({ ...formData, interim_hourly_rate: e.target.value })}
                      data-testid="emp-interim-rate"
                      placeholder="Ex : 22.50"
                    />
                  </div>
                  <div>
                    <Label>Agence d'intérim</Label>
                    <Input
                      value={formData.interim_agency_name}
                      onChange={(e) => setFormData({ ...formData, interim_agency_name: e.target.value })}
                      data-testid="emp-interim-agency"
                      placeholder="Nom de l'agence"
                    />
                  </div>
                  <div>
                    <Label>Téléphone agence</Label>
                    <Input
                      value={formData.interim_agency_phone}
                      onChange={(e) => setFormData({ ...formData, interim_agency_phone: e.target.value })}
                      data-testid="emp-interim-phone"
                    />
                  </div>
                  <div>
                    <Label>Email agence</Label>
                    <Input
                      type="email"
                      value={formData.interim_agency_email}
                      onChange={(e) => setFormData({ ...formData, interim_agency_email: e.target.value })}
                      data-testid="emp-interim-email"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Adresse agence</Label>
                    <Input
                      value={formData.interim_agency_address}
                      onChange={(e) => setFormData({ ...formData, interim_agency_address: e.target.value })}
                      data-testid="emp-interim-address"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button onClick={handleSubmit} data-testid="save-employee-btn">{editMode ? "Mettre à jour" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );

export default EmployeesPage;
